import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import JobCard from '../components/JobCard';
import { 
  Search, 
  MapPin, 
  Plus, 
  Filter,
  Wrench,
  Sparkles,
  Truck,
  MoreHorizontal,
  Loader2,
  AlertCircle,
  List,
  Map as MapIcon,
  DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Category config
const categoryConfig = {
  plumbing: { icon: Wrench, color: '#3B82F6', bgColor: 'bg-blue-500' },
  cleaning: { icon: Sparkles, color: '#10B981', bgColor: 'bg-emerald-500' },
  delivery: { icon: Truck, color: '#F59E0B', bgColor: 'bg-amber-500' },
  others: { icon: MoreHorizontal, color: '#8B5CF6', bgColor: 'bg-purple-500' }
};

// Custom marker icon with category color
const createCustomIcon = (color = '#0F172A', isHighlighted = false, isUrgent = false) => {
  const size = isHighlighted ? 44 : 36;
  const borderWidth = isHighlighted ? 4 : 2;
  const pulseClass = isUrgent ? 'urgent-pulse' : '';
  
  return L.divIcon({
    className: `custom-marker ${pulseClass}`,
    html: `
      <div style="
        background: ${color}; 
        width: ${size}px; 
        height: ${size}px; 
        border-radius: 50%; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        border: ${borderWidth}px solid white;
        transition: all 0.2s ease;
        ${isHighlighted ? 'transform: scale(1.2);' : ''}
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="0">
          <circle cx="12" cy="12" r="5"/>
        </svg>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2],
  });
};

// Map controller component for programmatic control
const MapController = ({ center, zoom, selectedJobId, jobs }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedJobId) {
      const job = jobs.find(j => j.job_id === selectedJobId);
      if (job?.location?.lat && job?.location?.lng) {
        map.flyTo([job.location.lat, job.location.lng], 15, {
          duration: 0.5
        });
      }
    }
  }, [selectedJobId, jobs, map]);

  return null;
};

// Location finder with better UX
const LocationFinder = ({ onLocationFound, setMapReady }) => {
  const map = useMap();

  useEffect(() => {
    setMapReady(true);
    
    map.locate({ setView: false, maxZoom: 13 });

    map.on('locationfound', (e) => {
      onLocationFound(e.latlng);
      map.flyTo(e.latlng, 12);
    });

    map.on('locationerror', () => {
      // Default to New York (Manhattan)
      const defaultLoc = { lat: 40.7580, lng: -73.9855 };
      onLocationFound(defaultLoc);
      map.flyTo([defaultLoc.lat, defaultLoc.lng], 12);
    });
  }, [map, onLocationFound, setMapReady]);

  return null;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter] = useState([40.7580, -73.9855]); // Manhattan default
  const [hoveredJobId, setHoveredJobId] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [viewMode, setViewMode] = useState('split'); // split | list | map

  const categories = [
    { value: 'all', label: t('jobs.filter_all'), icon: Filter },
    { value: 'plumbing', label: t('landing.plumbing'), icon: Wrench, color: '#3B82F6' },
    { value: 'cleaning', label: t('landing.cleaning'), icon: Sparkles, color: '#10B981' },
    { value: 'delivery', label: t('landing.delivery'), icon: Truck, color: '#F59E0B' },
    { value: 'others', label: t('landing.others'), icon: MoreHorizontal, color: '#8B5CF6' },
  ];

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/jobs?status=open&limit=50`;
      
      if (selectedCategory && selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }

      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, token]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleLocationFound = useCallback((latlng) => {
    setUserLocation(latlng);
  }, []);

  const filteredJobs = jobs.filter(job => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        job.title?.toLowerCase().includes(query) ||
        job.description?.toLowerCase().includes(query) ||
        job.location?.address?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Jobs with valid coordinates for the map
  const mappableJobs = filteredJobs.filter(job => 
    job.location?.lat && job.location?.lng
  );

  const getCategoryColor = (category) => {
    return categoryConfig[category]?.color || '#0F172A';
  };

  const handleJobHover = (jobId) => {
    setHoveredJobId(jobId);
  };

  const handleJobClick = (jobId) => {
    setSelectedJobId(jobId);
  };

  const handleMarkerClick = (jobId) => {
    setSelectedJobId(jobId);
    // Scroll the job card into view
    const element = document.getElementById(`job-card-${jobId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Sidebar - Job List */}
        <div className={`${viewMode === 'map' ? 'hidden lg:flex' : 'flex'} w-full lg:w-[420px] xl:w-[480px] bg-white border-r border-slate-200 flex-col h-[calc(100vh-4rem)]`}>
          {/* Header */}
          <div className="p-4 border-b border-slate-100 space-y-4 shrink-0">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                data-testid="job-search-input"
                type="text"
                placeholder={t('jobs.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-5 bg-slate-50 border-slate-200"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  data-testid={`filter-${cat.value}-btn`}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    selectedCategory === cat.value
                      ? 'bg-slate-900 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <cat.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{cat.label}</span>
                </button>
              ))}
            </div>

            {/* Post Job Button (for employers) */}
            {user?.role === 'employer' && (
              <Button
                data-testid="create-job-btn"
                onClick={() => navigate('/jobs/create')}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-5 shadow-md"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('jobs.create_job')}
              </Button>
            )}

            {/* Results count */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">
                {filteredJobs.length} {t('nav.jobs')} {mappableJobs.length > 0 && `• ${mappableJobs.length} on map`}
              </span>
              {/* Mobile view toggle */}
              <div className="flex lg:hidden gap-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-slate-200' : 'hover:bg-slate-100'}`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`p-2 rounded-lg ${viewMode === 'map' ? 'bg-slate-200' : 'hover:bg-slate-100'}`}
                >
                  <MapIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Job List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                <p className="mt-4 text-slate-500">{t('common.loading')}</p>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">{t('jobs.no_jobs')}</p>
                {user?.role === 'employer' && (
                  <Button 
                    onClick={() => navigate('/jobs/create')}
                    className="mt-4 bg-emerald-500 hover:bg-emerald-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('jobs.create_job')}
                  </Button>
                )}
              </div>
            ) : (
              filteredJobs.map((job, index) => (
                <div
                  key={job.job_id}
                  id={`job-card-${job.job_id}`}
                  onMouseEnter={() => handleJobHover(job.job_id)}
                  onMouseLeave={() => handleJobHover(null)}
                  onClick={() => handleJobClick(job.job_id)}
                  className={`transition-all duration-200 ${
                    selectedJobId === job.job_id ? 'ring-2 ring-emerald-500 ring-offset-2 rounded-xl' : ''
                  } ${hoveredJobId === job.job_id ? 'scale-[1.02]' : ''}`}
                >
                  <JobCard 
                    job={job} 
                    index={index}
                    onClick={() => navigate(`/jobs/${job.job_id}`)}
                    showMapPin={!!job.location?.lat}
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Map */}
        <div className={`${viewMode === 'list' ? 'hidden lg:block' : 'block'} flex-1 h-[60vh] lg:h-[calc(100vh-4rem)] relative`}>
          <MapContainer
            center={mapCenter}
            zoom={12}
            className="h-full w-full z-0"
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <LocationFinder onLocationFound={handleLocationFound} setMapReady={setMapReady} />
            <MapController selectedJobId={selectedJobId} jobs={mappableJobs} />

            {/* User location marker */}
            {userLocation && (
              <Marker
                position={[userLocation.lat, userLocation.lng]}
                icon={L.divIcon({
                  className: 'user-location-marker',
                  html: `
                    <div style="
                      width: 20px;
                      height: 20px;
                      background: #10B981;
                      border: 3px solid white;
                      border-radius: 50%;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    "></div>
                  `,
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                })}
              >
                <Popup>
                  <div className="text-center p-1">
                    <p className="font-semibold text-slate-900">{t('map.your_location')}</p>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Job markers */}
            {mappableJobs.map((job) => {
              const isHighlighted = hoveredJobId === job.job_id || selectedJobId === job.job_id;
              const category = categoryConfig[job.category] || categoryConfig.others;
              
              return (
                <Marker
                  key={job.job_id}
                  position={[job.location.lat, job.location.lng]}
                  icon={createCustomIcon(category.color, isHighlighted, job.urgent)}
                  eventHandlers={{
                    click: () => handleMarkerClick(job.job_id)
                  }}
                  zIndexOffset={isHighlighted ? 1000 : 0}
                >
                  <Popup>
                    <div className="p-1 min-w-[220px]">
                      {/* Category badge */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 ${category.bgColor} rounded-lg flex items-center justify-center`}>
                          <category.icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-xs text-slate-500 capitalize">{job.category}</span>
                        {job.urgent && (
                          <Badge variant="destructive" className="text-xs ml-auto">
                            {t('jobs.urgent')}
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="font-bold text-slate-900 mb-1">{job.title}</h3>
                      
                      <div className="flex items-center gap-1 text-emerald-600 font-bold text-lg mb-2">
                        <DollarSign className="w-4 h-4" />
                        {job.budget}
                      </div>
                      
                      <p className="text-xs text-slate-500 mb-3 flex items-start gap-1">
                        <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                        {job.location?.address}
                      </p>
                      
                      <Button
                        size="sm"
                        data-testid={`popup-view-job-${job.job_id}`}
                        onClick={() => navigate(`/jobs/${job.job_id}`)}
                        className="w-full bg-slate-900 hover:bg-slate-800"
                      >
                        {t('jobs.details')}
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>

          {/* Map legend */}
          <div className="absolute top-4 right-4 bg-white rounded-xl shadow-lg p-3 z-[1000]">
            <p className="text-xs font-semibold text-slate-700 mb-2">Categories</p>
            <div className="space-y-1">
              {Object.entries(categoryConfig).map(([key, config]) => (
                <div key={key} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: config.color }}
                  />
                  <span className="text-xs text-slate-600 capitalize">{key}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Map overlay info */}
          <div className="absolute bottom-6 left-6 right-6 lg:left-auto lg:right-6 lg:w-auto z-[1000]">
            <div className="glass rounded-xl p-4 shadow-lg flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {mappableJobs.length} {t('nav.jobs')} on map
                </p>
                <p className="text-xs text-slate-500">
                  Click markers to view details
                </p>
              </div>
            </div>
          </div>

          {/* Mobile back to list button */}
          <button
            onClick={() => setViewMode('list')}
            className="lg:hidden absolute top-4 left-4 bg-white rounded-full p-3 shadow-lg z-[1000]"
          >
            <List className="w-5 h-5 text-slate-700" />
          </button>
        </div>
      </div>
    </div>
  );
}
